import { css } from '@emotion/css'
import { Accordion, Center, useMantineTheme } from '@mantine/core'
import { OverviewColumns } from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import { SummaryTable } from 'components/SummaryTable/SummaryTable'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { ToggleButton } from 'components/ToggleButton/ToggleButton'
import { translate } from 'i18n/i18n'
import { capitalize, isEqual, uniqBy } from 'lodash'
import React, { memo, useEffect, useMemo, useState } from 'react'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TAppliedIrrigationActivityDisplayOption } from 'stores/smallStore/smallStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { PropertyAccordion } from './PropertyAccordion'
import { autoSortColumns, comboDataLayers } from './SectionTable'
import type {
  CombinedDataItem,
  CurrentView,
  ProcessedValueGroupArrays,
  RefactoredData,
  TLabelType,
  TPestToggleDisplayOption,
  TPointFlowRate,
  TValueGroupLabel,
} from './types'
import {
  filterEmptyColumns,
  filterEmptyRows,
  getPropertyHemisphereByPropertyName,
  getPropertyIdByPropertyName,
} from './utils'

const LayerAccordion = ({
  sectionIndex,
  layer,
  transformedData,
  processedValueGroupArrays,
  selectedValueGroupsArray,
  timestamp,
  insectIds,
  flowRates,
  isLoading,
  totalLayers,
}: {
  sectionIndex: number
  layer: string
  transformedData: RefactoredData
  processedValueGroupArrays: ProcessedValueGroupArrays
  selectedValueGroupsArray: TValueGroupLabel[]
  timestamp: string
  insectIds: number[]
  flowRates: TPointFlowRate[]
  isLoading: boolean
  totalLayers: number
}) => {
  const theme = useMantineTheme()
  const isWideScreen = useIsWideScreen()

  // cells format their values in the user's units when they render, so re-render when the units change
  userDetailsStore.useSelector(
    ({ depth, fertigation, flowRate, pressure, rain, speed, temperature, volume }) => ({
      depth,
      fertigation,
      flowRate,
      pressure,
      rain,
      speed,
      temperature,
      volume,
    }),
  )

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const userProperties = Object.values(properties).length

  const [currentView, setCurrentView] = useState<CurrentView>({
    pest: 'BLOCK',
    irrigation: 'VOLUME',
  })

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({})

  const getDataLayerKey = (layer: string): string => {
    const displayToDataKey: Record<string, string> = {
      ['Crop Development'.toLowerCase()]: 'cropDevelopment',
      ['Water Management'.toLowerCase()]: 'waterManagement',
    }

    return displayToDataKey[layer.toLowerCase()] || layer.toLowerCase()
  }

  const getDisplayName = (key: string): string => {
    const dataKeyToDisplay: Record<string, string> = {
      cropDevelopment: translate.phrases.banyanApp('Crop Development'),
      waterManagement: translate.phrases.banyanApp('Water Management'),
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return dataKeyToDisplay[key] || translate.phrases.banyanApp(capitalize(key) as any)
  }

  const dataLayerKey = getDataLayerKey(layer.toLowerCase())
  const normalizedLayer = getDataLayerKey(layer)
  const layerValueGroups = processedValueGroupArrays[normalizedLayer] || selectedValueGroupsArray
  const hasTablePerValueGroup = dataLayerKey === 'pests' || comboDataLayers.includes(dataLayerKey)

  const relevantProperties = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(transformedData).filter(([_, propertyData]) => {
        return propertyData[dataLayerKey]?.length > 0
      }),
    [transformedData, dataLayerKey],
  )

  const renderTable = (
    propertyName: string,
    tableData: CombinedDataItem[],
    valueGroups: TValueGroupLabel[],
    label?: string,
    irrigationPestOrNone: TLabelType = 'none',
  ) => {
    let filteredTableData = tableData

    if (normalizedLayer === 'pests' && valueGroups?.[0]?.value !== 'trap_catches') {
      if (currentView.pest === 'BLOCK') {
        filteredTableData = tableData.filter((row) => row.type !== 'summary')
      } else if (currentView.pest === 'CROP') {
        const currentPest = valueGroups?.[0]?.value
        const insectId = currentPest?.split('_')[1]

        filteredTableData = tableData.filter((row) => row.type === 'summary' && row.insectId === insectId)
      }
    }

    if (!filteredTableData || filteredTableData.length === 0) return { table: null, columns: [] }

    const allColumns = OverviewColumns({
      view: normalizedLayer,
      selectedValueGroupsArray: valueGroups,
      currentView,
      insectIds,
      pointsFlowRates: flowRates,
      timestamp,
      isSouthernHemisphere: getPropertyHemisphereByPropertyName(propertyName, properties),
      propertyId: getPropertyIdByPropertyName(propertyName, properties),
    })

    const columnsWithData = hasTablePerValueGroup
      ? allColumns
      : filterEmptyColumns(allColumns, filteredTableData)

    const filteredColumns = columnsWithData.filter((col: TColumn) => visibleColumns[col.key] !== false)

    if (!filteredColumns.length) return { table: null, columns: [] }

    const tableKey = `${label}-${valueGroups.map((vg) => vg.value).join('-')}`

    return {
      table: (
        <>
          <div
            key={tableKey}
            className={css({
              display: 'flex',
              flexDirection: 'column',
              opacity: 1,
              height: 'auto',
              transition: '200ms ease all',
              overflow: 'unset',
              boxSizing: 'border-box',
              border: `0.5px solid ${theme.colors.grey[2]}`,
              borderCollapse: 'collapse',
            })}
          >
            {label && (
              <div
                css={{
                  padding: '10px',
                  backgroundColor: colors.grey200,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {label}
                <div css={{ marginLeft: 'auto' }}>
                  {irrigationPestOrNone === 'pests' && (
                    <ToggleButton
                      buttonProps={{ size: 'xs', className: css({ fontSize: 13 }) }}
                      data={[
                        { label: translate.phrases.banyanApp('Block View'), value: 'BLOCK' },
                        { label: translate.phrases.banyanApp('Crop View'), value: 'CROP' },
                      ]}
                      onChange={(newValue: TPestToggleDisplayOption) =>
                        setCurrentView((prevState) => ({ ...prevState, pest: newValue }))
                      }
                      value={currentView.pest}
                    />
                  )}
                  {irrigationPestOrNone === 'irrigation' && (
                    <ToggleButton
                      buttonProps={{ size: 'xs', className: css({ fontSize: 13 }) }}
                      data={[
                        { label: translate.phrases.banyanApp('Volume'), value: 'VOLUME' },
                        { label: translate.phrases.banyanApp('Duration'), value: 'DURATION' },
                      ]}
                      onChange={(newValue: TAppliedIrrigationActivityDisplayOption) => {
                        setCurrentView((prevState) => ({
                          ...prevState,
                          irrigation: newValue,
                        }))

                        smallStore.setState((s) => ({
                          ...s,
                          appliedIrrigationActivityDisplay: newValue,
                        }))
                      }}
                      value={currentView.irrigation}
                    />
                  )}
                </div>
              </div>
            )}
            <SummaryTable
              type={'values'}
              autoSortColumns={[
                ...autoSortColumns,
                `pointName_${normalizedLayer}`,
                `blockName_${normalizedLayer}`,
              ]}
              columns={filteredColumns}
              data={filteredTableData}
              defaultSortedColumns={['pointName', 'blockName', 'crop', 'device']}
            />
          </div>
          <br />
        </>
      ),
      columns: filteredColumns,
    }
  }

  const sortedProperties = relevantProperties.sort(([propertyNameA], [propertyNameB]) =>
    propertyNameA.localeCompare(propertyNameB),
  )

  const propertyNames = sortedProperties.map(([propertyName]) => propertyName)

  const [openProperties, setOpenProperties] = useState<string[]>(() => {
    if (userProperties < 30 && sectionIndex === 0) {
      // All properties open in the first LayerAccordion
      return propertyNames
    } else {
      // Nothing open by default elsewhere
      return []
    }
  })

  useEffect(() => {
    if (userProperties < 30 && sectionIndex === 0) {
      setOpenProperties(propertyNames)
    } else {
      setOpenProperties([])
    }
  }, [userProperties, sectionIndex, JSON.stringify(propertyNames)])

  if (isLoading) {
    return null
  }

  const noDataLabel = (
    <div css={{ padding: '20px', color: '#ababab' }}>{translate.phrases.banyanApp('No data found.')}</div>
  )

  if (sortedProperties.length === 0) {
    // If there's only one layer shown, just display the text
    if (totalLayers === 1) return noDataLabel

    // If there are multiple layers, show "No data available" in the panel
    return (
      <Accordion.Item value={layer}>
        <Center
          css={{
            backgroundColor: colors.grey200,
            justifyContent: 'space-between',
            display: 'flex',
            width: '100%',
          }}
        >
          <Accordion.Control
            css={{
              'backgroundColor': colors.grey200,
              'flex': 1,
              '&:hover': {
                backgroundColor: `${colors.grey200} !important`,
              },
            }}
          >
            {getDisplayName(dataLayerKey)}
          </Accordion.Control>
        </Center>
        <Accordion.Panel style={{ position: 'relative' }}>{noDataLabel}</Accordion.Panel>
      </Accordion.Item>
    )
  }

  return (
    <Accordion.Item value={layer}>
      <Center
        css={{
          backgroundColor: colors.grey200,
          justifyContent: 'space-between',
          display: 'flex',
          width: '100%',
        }}
      >
        <Accordion.Control
          css={{
            'backgroundColor': colors.grey200,
            'flex': 1,
            '&:hover': {
              backgroundColor: `${colors.grey200} !important`,
            },
          }}
        >
          {getDisplayName(dataLayerKey)}
        </Accordion.Control>
      </Center>
      <Accordion.Panel style={{ position: 'relative' }}>
        <Accordion
          multiple
          value={openProperties}
          onChange={setOpenProperties}
          key={JSON.stringify(sortedProperties.map(([propertyName]) => propertyName))}
        >
          {sortedProperties.map(([propertyName, propertyData], idx) => {
            const isOpen = openProperties.includes(propertyName)

            const singleTableColumns = hasTablePerValueGroup
              ? []
              : OverviewColumns({
                  view: normalizedLayer,
                  selectedValueGroupsArray: layerValueGroups,
                  currentView,
                  insectIds,
                  pointsFlowRates: flowRates,
                  timestamp,
                  isSouthernHemisphere: getPropertyHemisphereByPropertyName(propertyName, properties),
                  propertyId: getPropertyIdByPropertyName(propertyName, properties),
                })

            const singleTableRows = hasTablePerValueGroup
              ? []
              : filterEmptyRows(propertyData[dataLayerKey], singleTableColumns, normalizedLayer)

            let tablesAndColumns: { table: React.ReactNode; columns: TColumn[] }[]

            if (!isOpen) {
              tablesAndColumns = []
            } else if (hasTablePerValueGroup) {
              const specificSelectedValueGroupsArray = processedValueGroupArrays[normalizedLayer]

              // For each selected value group, create a table and collect its columns
              tablesAndColumns = specificSelectedValueGroupsArray
                .map((selectedValueGroup) => {
                  const tableData = propertyData[dataLayerKey].filter((item) => {
                    if (selectedValueGroup.value === 'trap_catches') {
                      return item.type === 'property'
                    }

                    return true
                  })

                  const columns = OverviewColumns({
                    view: normalizedLayer,
                    selectedValueGroupsArray: [selectedValueGroup],
                    currentView,
                    insectIds,
                    pointsFlowRates: flowRates,
                    timestamp,
                    isSouthernHemisphere: getPropertyHemisphereByPropertyName(propertyName, properties),
                    propertyId: getPropertyIdByPropertyName(propertyName, properties),
                  })

                  const filteredData = filterEmptyRows(tableData, columns, normalizedLayer)

                  if (!filteredData || filteredData.length === 0) return null

                  const isPest = dataLayerKey === 'pests' && selectedValueGroup.value !== 'trap_catches'
                  const isIrrigation = selectedValueGroup.value === 'irrigation_activity'

                  const layerLabel = isPest
                    ? `${selectedValueGroup.label} ${translate.phrases.banyanApp('Degree Days')}`
                    : selectedValueGroup.label

                  let isIrrigationPestOrNone: TLabelType = 'none'

                  if (isPest) {
                    isIrrigationPestOrNone = 'pests'
                  } else if (isIrrigation) {
                    isIrrigationPestOrNone = 'irrigation'
                  }

                  return renderTable(
                    propertyName,
                    filteredData,
                    [selectedValueGroup],
                    layerLabel,
                    isIrrigationPestOrNone,
                  )
                })
                .filter((item) => item !== null && item.table !== null) as {
                table: React.ReactNode
                columns: TColumn[]
              }[]
            } else {
              // Just one table/columns for non-pests
              const { table, columns } = renderTable(propertyName, singleTableRows, layerValueGroups)

              tablesAndColumns = [{ table, columns }]
            }

            //get all possible columns for this property for the menu
            const allPossibleColumns = hasTablePerValueGroup
              ? (processedValueGroupArrays[normalizedLayer] || selectedValueGroupsArray).flatMap(
                  (selectedValueGroup) =>
                    OverviewColumns({
                      view: normalizedLayer,
                      selectedValueGroupsArray: [selectedValueGroup],
                      currentView,
                      insectIds,
                      timestamp,
                      pointsFlowRates: flowRates,
                      isSouthernHemisphere: getPropertyHemisphereByPropertyName(propertyName, properties),
                      propertyId: getPropertyIdByPropertyName(propertyName, properties),
                    }),
                )
              : filterEmptyColumns(singleTableColumns, singleTableRows)

            // De-duplicate columns by key
            const allColumnsForMenu = uniqBy(allPossibleColumns, (col) => col.key).map(({ key, title }) => ({
              key,
              title,
            }))

            // Gather all rendered tables, as before
            const renderedTables = tablesAndColumns
              .filter((item) => item.table !== null)
              .map((item, i) => <React.Fragment key={i}>{item.table}</React.Fragment>)

            return (
              <PropertyAccordion
                key={`${propertyName}-${idx}`}
                propertyName={propertyName}
                propertyId={getPropertyIdByPropertyName(propertyName, properties)}
                layer={layer}
                selectedValueGroupsArray={selectedValueGroupsArray}
                currentView={currentView}
                insectIds={insectIds}
                flowRates={flowRates}
                timestamp={timestamp}
                visibleColumns={visibleColumns}
                onVisibleColumnsChange={setVisibleColumns}
                isSouthernHemisphere={getPropertyHemisphereByPropertyName(propertyName, properties)}
                columns={allColumnsForMenu}
                isWideScreen={isWideScreen}
              >
                {isOpen && renderedTables}
              </PropertyAccordion>
            )
          })}
        </Accordion>
      </Accordion.Panel>
    </Accordion.Item>
  )
}

const MemoizedLayerAccordion = memo(LayerAccordion, isEqual) as typeof LayerAccordion

export { MemoizedLayerAccordion as LayerAccordion }
